import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { WizardHeader } from '../../../../common/components/wizardHeader/WizardHeader'

import { FullPageFormLayout, Loader, Modal, AcknowledgeModal } from '@percent/lemonade'
import { CurrencyCode, formatMoney } from '@percent/utility'
import {
  useAuthState,
  useQueryList,
  useMutation,
  useDonationPartnerConfig
} from '@percent/partner-dashboard/common/hooks'
import { subsidiariesService } from '@percent/partner-dashboard/services/subsidiaries/subsidiariesService'
import { percentClient } from '@percent/partner-dashboard/common/HOC/withAxios/WithAxios'
import { CreateDonationRequest } from '@percent/partner-dashboard/services/donation/donationService.types'
import styles from './CorporateDonationContainer.module.scss'
import { CorporateDonationForm, GIFT_NOTE_MAX_LENGTH } from './CorporateDonationForm/CorporateDonationForm'
import { CorporateDonationFormValues } from './CorporateDonationForm/CorporateDonationForm.types'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { OrganisationUnion } from '@percent/partner-dashboard/services/nonprofits/nonprofitsService.types'

export function CorporateDonationContainer() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { partner, user } = useAuthState()
  const {
    donationService: { createDonation }
  } = useServices()

  const [{ data: subsidiariesData, isLoading: isSubsidiariesLoading }] = useQueryList(
    (params: { partnerId: string; pageSize: number }) => subsidiariesService({ percentClient }).getSubsidiaries(params),
    { partnerId: user?.partnerId || '', pageSize: 100 },
    !!user?.partnerId
  )

  const [{ data: partnerConfigData }] = useDonationPartnerConfig()

  const intlConfigLang = i18n.language

  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [selectedOrganisation, setSelectedOrganisation] = useState<OrganisationUnion | null>(null)

  const [{ isLoading }, { apiFunc }] = useMutation(
    createDonation,
    () => {
      setIsSuccess(true)
    },
    error => {
      const errorResponse = error?.response?.data?.error

      if (errorResponse?.code === 'donation/not_allowed_organisation_country_currency') {
        setErrorMessage(errorResponse.message)
      } else {
        setErrorMessage(t('dialog.donation.error.description'))
      }

      setIsError(true)
    }
  )

  const handleCloseSuccessModal = () => {
    setIsSuccess(false)
    navigate('/donations')
  }

  const handleCloseErrorModal = () => {
    setIsError(false)
    setErrorMessage('')
  }

  const initialCurrency = partner?.currencyCode as CurrencyCode

  const { errors, values, handleChange, handleSubmit, handleBlur, touched, setFieldValue, setFieldTouched } =
    useFormik<CorporateDonationFormValues>({
      initialValues: {
        country: '',
        organization: '',
        subsidiary: '',
        amount: 0,
        currency: initialCurrency,
        costCenter: '',
        giftNote: ''
      },
      validationSchema: Yup.object().shape({
        country: Yup.string().required(t('errorMessage.required')),
        organization: Yup.string().required(t('errorMessage.required')),
        subsidiary: Yup.string(),
        amount: Yup.number().required(t('errorMessage.required')).positive(t('errorMessage.postiveNumber')),
        currency: Yup.string().required(),
        costCenter: Yup.string().max(255, t('errorMessage.shouldNotExceed255')),
        giftNote: Yup.string().max(GIFT_NOTE_MAX_LENGTH, t('errorMessage.maxLength', { length: GIFT_NOTE_MAX_LENGTH }))
      }),
      validateOnMount: false,
      validateOnChange: false,
      validateOnBlur: true,
      onSubmit: async ({ amount, currency, organization, subsidiary, costCenter, giftNote }) => {
        const payload: CreateDonationRequest = {
          amount,
          currencyCode: currency,
          organisationId: organization,
          ...(subsidiary && { subsidiaryId: subsidiary }),
          metadata: costCenter ? { costCenter } : undefined,
          ...(giftNote && { giftNote }),
          category: 'corporate_donation'
        }

        apiFunc(payload)
      }
    })

  const selectedSubsidiary = subsidiariesData?.find(sub => sub.id === values.subsidiary)

  const defaultCurrency: CurrencyCode = useMemo(() => {
    if (selectedSubsidiary?.currency) {
      return selectedSubsidiary.currency as CurrencyCode
    }

    return partner?.currencyCode as CurrencyCode
  }, [selectedSubsidiary?.currency, partner?.currencyCode])

  // Keep currency in sync in an effect rather than in onSubsidiaryChange.
  // Changing currency remounts MoneyInput; updating it in the same batch as the
  // subsidiary change can race with that remount and make the amount field flaky.
  useEffect(() => {
    setFieldValue('currency', defaultCurrency)
  }, [defaultCurrency, setFieldValue])

  const currencies: CurrencyCode[] = useMemo(() => {
    const partnerCurrency = partner?.currencyCode as CurrencyCode | undefined

    // If subsidiary is selected, use subsidiary currencies
    if (selectedSubsidiary) {
      const subsidiaryCurrencies = [selectedSubsidiary.currency, selectedSubsidiary.backupCurrency]
        .filter(Boolean)
        .map(currency => currency as CurrencyCode)

      if (subsidiaryCurrencies.length > 0) return subsidiaryCurrencies
    }

    // If no subsidiary, use supported currencies from partner config
    const supportedCurrencies = partnerConfigData?.supported_currencies?.value

    if (supportedCurrencies && supportedCurrencies.length > 0) {
      return supportedCurrencies as CurrencyCode[]
    }

    // Fallback to partner's default currency
    return partnerCurrency ? [partnerCurrency] : []
  }, [partner?.currencyCode, selectedSubsidiary, partnerConfigData])

  const handleCancel = () => {
    navigate('/donations')
  }

  const isFormValid = useMemo(() => {
    const hasRequiredFields = values.country && values.organization && values.amount > 0
    const hasNoValidationErrors = Object.keys(errors).length === 0

    return hasRequiredFields && hasNoValidationErrors
  }, [values.country, values.organization, values.amount, errors])

  if (isSubsidiariesLoading) {
    return <Loader />
  }

  return (
    <div className={styles.corporateDonationWrapper}>
      <FullPageFormLayout
        actionsHeader={
          <WizardHeader
            title={t('corporateDonation.pageTitle')}
            onCancel={handleCancel}
            onPublish={handleSubmit}
            isPublishDisabled={!isFormValid || isLoading}
            isLoading={isLoading}
            publishBtnCopy={t('corporateDonation.button.create')}
            toolTipCopy={isFormValid ? '' : t('toolTip.requiredFields')}
            publishBtnTestId="add-funds-btn"
            cancelBtnTestId="cancel-button"
          />
        }
      >
        <CorporateDonationForm
          errors={errors}
          values={values}
          handleChange={handleChange}
          handleBlur={handleBlur}
          touched={touched}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          defaultCurrency={defaultCurrency}
          currencies={currencies}
          intlConfigLang={intlConfigLang}
          subsidiariesData={subsidiariesData}
          selectedOrganisation={selectedOrganisation}
          setSelectedOrganisation={setSelectedOrganisation}
          onSubsidiaryChange={(subsidiaryId: string) => {
            setFieldValue('subsidiary', subsidiaryId)
            setFieldTouched('subsidiary', true)
          }}
        />
      </FullPageFormLayout>
      <Modal
        open={isSuccess}
        onClose={handleCloseSuccessModal}
        aria-labelledby="corporate-donation-success"
        data-testid="success-modal"
      >
        <AcknowledgeModal
          result="positive"
          title={t('dialog.donation.success.title')}
          description={t('dialog.donation.success.description', {
            amount: formatMoney(
              { amount: values.amount, currency: values.currency },
              { locale: i18n.language, hideDecimalsIfZero: true }
            ),
            organizationName: selectedOrganisation?.name
          })}
          buttonText={t('button.close')}
          handleClose={handleCloseSuccessModal}
          buttonTestId="success-modal-btn"
        />
      </Modal>

      <Modal
        open={isError}
        onClose={handleCloseErrorModal}
        aria-labelledby="corporate-donation-error"
        data-testid="error-modal"
      >
        <AcknowledgeModal
          result="negative"
          title={t('dialog.donation.error.title')}
          description={errorMessage}
          buttonText={t('button.close')}
          handleClose={handleCloseErrorModal}
          buttonTestId="error-modal-btn"
        />
      </Modal>
    </div>
  )
}
